import { Player } from './Player';

/**
 * From player the below can be subscribed/unsubscribed.
 *
 * @usageNotes
 * Sample usage
 * ### subscribe
 * ```
 *  adPlayer.subscribe('playbackstatechanged', state => {
 *    console.log(state);
 *  });
 *  adPlayer.subscribe('progressupdate', function() {
 *    slider.value = videoElement.currentTime;
 *    timeElement.innerHTML = videoElement.currentTime.toFixed(2) + '/' + videoElement.duration.toFixed(2);
 *  });
 *
 * ```
 *
 *  ### unsubscribe
 *
 * @publicApi
 *
 *  adPlayer.unsubscribe('statechanged');
 *  adPlayer.unsubscribe('progressupdate');
 */

/**
 * Indicates there was a track switch
 *
 * @event player#trackschanged
 */

/**
 * Fired when the player has changed its states
 * and will remain in that state until a new state change is made.
 *
 * @event player#playbackstatechanged
 */

/**
 * Fired when the seeking state has changed
 * and will remain in that state until a new state change is made.
 *
 * @event player#bufferingstatechanged
 */

/**
 * Fired when the seeking state has changed
 * and will remain in that state until a new state change is made.
 *
 * @event player#seekingstatechanged
 */

/**
 * Indicates that track information is available
 *
 * @event player#trackavailabilitychanged
 */

/**
 * Indicates there was a playback variant change. It does not
 * fire when the application calls selectTextTrack(),
 * selectAudioLanguage() or selectTextLanguage().
 *
 * Note that AdaptationEvents are not fired for manual track selections.
 *
 * @event player#bandwidthchanged
 */

/**
 * The timeupdate event is fired when the time indicated
 * by the video currentTime attribute has been updated.
 *
 * @event player#progessupdate
 */

/**
 * Indicates an error has occurred
 *
 * @event player#error
 */

/**
 * Indicates the presence of some timed meta data such as an EMSG box
 *
 * @event player#timedmetadata
 *
 */

/**
 * Indicates an error has occurred while processing ads
 *
 * @event player#aderror
 *
 */

/**
 * Fired when an individual ad has loaded and its metadata is available.
 *
 * @event player#adloaded
 */

/**
 * Fired when the ad's current time value changes.
 *
 * @event player#adprogressupdate
 */

/**
 * Fired when the ad player has changed its states
 * and will remain in that state until a new state change is made.
 *
 * @event player#adstatechanged
 */

/**
 * Fired when the stream has ended
 *
 @event player#streamended
 *
 */

/**
 * A Player interface defining Adverts specific functionality such as skip Ad, discard Ad breaks, etc.,
 * and properties of currently playing advert content.
 */
export interface AdPlayer extends Player {
  /** Whether the active ad manager came from preroll prefetch; undefined until known or when unsupported. */
  readonly isAdPrefetch?: boolean;

  /**
   * @property remainingTime Provides the remaining playback time for current ad.
   */
  remainingTime: number;

  /**
   * @property adInsertionType Indicates the type of ad insertion technology being used.
   * - CSAI (Client-Side Ad Insertion): Ads are stitched on the client side
   * - SSAI (Server-Side Ad Insertion): Ads are stitched on the server side
   */
  adInsertionType?: AdTechnology;

  /**
   * @method Skips the current ad (if skippable), else is a no-op
   */
  skipAd(): void;

  /**
   * @method If an ad break is currently playing, discard it and resume content. Otherwise, ignore the next scheduled ad break.
   */
  discardAdBreak(): void;

  /**
   * @method Returns an array of offsets in seconds indicating when a scheduled ad break will play. A preroll is represented by 0, and a postroll is represented by -1. An empty array indicates the ad or ad pod has no schedule and can be played at any time.
   */
  getCuePoints(): number[];

  /**
   * @method resizes the ad container to the specific size.
   * @param width of the ad slot.
   * @param height of the ad slot.
   * @param isFullscreen whether the player is in full screen mode.
   */
  resize(width: number, height: number, isFullscreen: boolean): void;
}

/**
 * Enum representing the type of ad insertion technology
 * @enum {string}
 */
export enum AdTechnology {
  CSAI = 'csai',
  SSAI = 'ssai',
}
